/**
 * The "go to the old / new UI" actions behind every switch control (new-UI release plan, 2026-10-10).
 * The RULES live in uiVersion.js (import-free, node-tested); this file is the part that touches the
 * browser: it stores the choice, counts the switch in LogRocket and reloads the page.
 *
 *   • A Super Admin (Teamio staff) keeps the separate per-area switches exactly as before: the choice
 *     is stored with setAreaUiChoice() and the CALLER re-renders (returns 'rerender', no reload).
 *   • An office user has ONE choice per employee for every area at once (setOfficeChoice), and an
 *     office switch always ends in a full page reload — that avoids the duplicate notification
 *     listeners a swap-in-place of the shell would leave behind, and no page is left half-switched.
 *     Because a reload loses anything half-typed, the person is ASKED first (uiCopy.js
 *     officeSwitchConfirmText). Declined ⇒ 'cancelled': nothing stored, counted or reloaded, and the
 *     caller does nothing (no counter bump). A Super Admin's switch never asks and never reloads.
 *
 * `storage` defaults to browserStorage(); `reload` and `confirm` are injectable so a spec never reloads
 * the runner or reaches the browser's own window.confirm.
 */
import LogRocket from 'logrocket';
import { setAreaUiChoice, setOfficeChoice, browserStorage } from './uiVersion.js';
import { officeSwitchConfirmText } from './uiCopy.js';

function defaultReload() {
  window.location.reload();
}

function defaultConfirm(message) {
  return window.confirm(message);
}

/** Counts one office switch. LogRocket runs on prod only and samples sessions, so this is a signal. */
function trackSwitch(target, from) {
  try { LogRocket.track('ui-switch', { to: target, from }); } catch (_e) { /* not initialised */ }
}

/**
 * Asks "the page will reload", then stores an office employee's choice, counts it and reloads.
 *   'refused'   — the target is not 'old' / 'new', there is no employee ID (the person is NOT asked), or
 *                 storage would not take the choice (nothing is reloaded then);
 *   'cancelled' — the person declined: nothing stored, counted or reloaded;
 *   'reload'    — stored, counted, `onConfirmed()` run, page reloading.
 * `onConfirmed` runs after the store and before the reload (the welcome banner marks itself seen there);
 * a throw inside it cannot block the reload.
 */
export function switchOfficeUi(target, {
  userKey, storage = browserStorage(), reload = defaultReload, from = 'menu', confirm = defaultConfirm, onConfirmed,
} = {}) {
  if (target !== 'old' && target !== 'new') return 'refused';
  if (userKey === null || userKey === undefined || String(userKey).trim() === '') return 'refused';
  if (!confirm(officeSwitchConfirmText(target))) return 'cancelled';
  if (!setOfficeChoice(storage, userKey, target)) return 'refused';
  if (typeof onConfirmed === 'function') {
    try { onConfirmed(); } catch (_e) { /* a callback's failure must not strand the person before the reload */ }
  }
  trackSwitch(target, from);
  reload();
  return 'reload';
}

/**
 * A page's "Classic view" / "New view" link. Super Admin ⇒ the per-area choice is stored and 'rerender'
 * is returned (the page bumps its version counter) — no confirm, no reload. Office user ⇒ switchOfficeUi:
 * 'reload', 'cancelled' or 'refused'.
 */
export function switchUi(pageKey, target, {
  isSuperAdmin, userKey, storage = browserStorage(), reload = defaultReload, confirm = defaultConfirm, onConfirmed,
} = {}) {
  if (isSuperAdmin === true) {
    setAreaUiChoice(pageKey, storage, target);
    return 'rerender';
  }
  return switchOfficeUi(target, { userKey, storage, reload, from: pageKey, confirm, onConfirmed });
}
